import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Button, Chip, MenuItem, Stack, TextField, Typography } from '@mui/material'
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded'
import VerifiedRoundedIcon from '@mui/icons-material/VerifiedRounded'
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded'
import MerchantToolShell, { shellStyles } from './MerchantToolShell'
import { useLanguage } from '../../contexts/LanguageContext'
import api from '../../services/api'

const gates = ['Gate A', 'Gate B', 'Gate C', 'Gate D']

export default function MerchantScanTicket() {
    const { lang } = useLanguage()
    const isFr = lang === 'fr'
    const scanInputRef = useRef(null)
    const [events, setEvents] = useState([])
    const [eventId, setEventId] = useState('')
    const [gateName, setGateName] = useState('Gate A')
    const [staffName, setStaffName] = useState('')
    const [staffCode, setStaffCode] = useState('')
    const [ticketCode, setTicketCode] = useState('')
    const [result, setResult] = useState(null)
    const [loadingEvents, setLoadingEvents] = useState(true)
    const [gateCounts, setGateCounts] = useState({})
    const [attendanceCount, setAttendanceCount] = useState(0)
    const [canManageStaff, setCanManageStaff] = useState(false)
    const [gateStaff, setGateStaff] = useState([])
    const [staffInvite, setStaffInvite] = useState({ name: '', email: '', phone: '' })
    const [savingStaff, setSavingStaff] = useState(false)

    const event = useMemo(
        () => events.find((item) => String(item.id) === String(eventId)) || null,
        [eventId, events]
    )

    useEffect(() => {
        let mounted = true
        setLoadingEvents(true)
        api.get('/merchant/ticket-check-in/events')
            .then((response) => {
                if (!mounted) return
                const rows = Array.isArray(response.data?.data) ? response.data.data : []
                setEvents(rows)
                setEventId((current) => current || rows[0]?.id || '')
                setLoadingEvents(false)
            })
            .catch((error) => {
                if (!mounted) return
                setLoadingEvents(false)
                setResult({
                    tone: 'error',
                    title: isFr ? 'Evenements indisponibles' : 'Events unavailable',
                    body: error.response?.data?.message || error.message,
                })
            })

        api.get('/merchant/gate-staff')
            .then((response) => {
                if (!mounted) return
                setGateStaff(Array.isArray(response.data?.data) ? response.data.data : [])
                setCanManageStaff(true)
            })
            .catch(() => {
                if (!mounted) return
                setGateStaff([])
                setCanManageStaff(false)
            })

        return () => {
            mounted = false
        }
    }, [isFr])

    useEffect(() => {
        scanInputRef.current?.focus()
    }, [eventId, result])

    const recordSuccessfulCheckIn = (gate) => {
        setGateCounts((current) => ({
            ...current,
            [gate]: (current[gate] || 0) + 1,
        }))
        setAttendanceCount((current) => current + 1)
    }

    const handleValidate = async () => {
        if (!event) {
            setResult({
                tone: 'warning',
                title: isFr ? 'Choisissez un evenement' : 'Choose an event',
                body: isFr
                    ? 'Creez ou choisissez un evenement avant de scanner.'
                    : 'Create or choose an event before scanning.',
            })
            return
        }

        if (!staffName.trim()) {
            setResult({
                tone: 'warning',
                title: isFr ? 'Acces personnel requis' : 'Staff access required',
                body: isFr
                    ? 'Ajoutez le nom du personnel de porte.'
                    : 'Enter the gate staff name.',
            })
            return
        }

        if (!ticketCode.trim()) {
            setResult({
                tone: 'warning',
                title: isFr ? 'Code requis' : 'Ticket code required',
                body: isFr
                    ? 'Scannez un billet ou saisissez le code manuellement.'
                    : 'Scan a ticket or enter the code manually.',
            })
            return
        }

        try {
            const response = await api.post('/merchant/tickets/validate', {
                event_id: event.id,
                ticket_code: ticketCode.trim(),
                gate_name: gateName,
                staff_name: staffName.trim(),
                device_id: 'web-usb-scanner',
                source: 'merchant_web_usb',
            })
            const ticket = response.data?.ticket || {}
            setResult({
                tone: 'success',
                title: isFr ? 'Billet accepte' : 'Ticket accepted',
                body: response.data?.message || (isFr ? 'Billet valide.' : 'Ticket is valid.'),
                details: ticket,
            })
            setTicketCode('')
            recordSuccessfulCheckIn(gateName)
        } catch (error) {
            const status = error.response?.status
            const data = error.response?.data || {}
            setResult({
                tone: status === 409 ? 'warning' : 'error',
                title:
                    data.result === 'ALREADY_CHECKED_IN'
                        ? (isFr ? 'Deja enregistre' : 'Already checked in')
                        : data.result === 'WRONG_EVENT'
                            ? (isFr ? 'Mauvais evenement' : 'Wrong event')
                            : (isFr ? 'Billet refuse' : 'Ticket rejected'),
                body: data.message || error.message,
                details: data.ticket || null,
            })
        }
    }

    const handleSaveStaff = async () => {
        if (!staffInvite.email.trim() && !staffInvite.phone.trim()) {
            setResult({
                tone: 'warning',
                title: isFr ? 'Contact requis' : 'Contact required',
                body: isFr ? 'Ajoutez l email ou le telephone du personnel.' : 'Enter the staff email or phone number.',
            })
            return
        }

        setSavingStaff(true)
        try {
            const response = await api.post('/merchant/gate-staff', {
                staff_name: staffInvite.name.trim(),
                staff_email: staffInvite.email.trim(),
                staff_phone: staffInvite.phone.trim(),
                event_id: eventId || null,
                gate_name: gateName,
            })
            setGateStaff((current) => [response.data?.staff, ...current].filter(Boolean))
            setStaffInvite({ name: '', email: '', phone: '' })
            setResult({
                tone: 'success',
                title: isFr ? 'Acces ajoute' : 'Access added',
                body: response.data?.message || (isFr ? 'Personnel autorise.' : 'Gate staff access saved.'),
            })
        } catch (error) {
            setResult({
                tone: 'error',
                title: isFr ? 'Acces non ajoute' : 'Access not saved',
                body: error.response?.data?.message || error.message,
            })
        } finally {
            setSavingStaff(false)
        }
    }

    return (
        <MerchantToolShell
            eyebrow={isFr ? 'Validation billets' : 'Ticket validation'}
            title={isFr ? 'Scanner billet' : 'Scan Ticket'}
            subtitle={
                isFr
                    ? 'Choisissez l evenement, la porte et le personnel. Cette vue reste limitee a la validation des billets.'
                    : 'Choose the event, gate, and staff. This view stays focused on ticket validation only.'
            }
            chips={[
                isFr ? 'Controle entree' : 'Gate control',
                event?.country || (isFr ? 'Pays evenement' : 'Event country'),
                isFr ? 'Mode scanner' : 'Scanner mode',
            ]}
            primaryAction={{
                label: isFr ? 'Valider billet' : 'Validate Ticket',
                onClick: handleValidate,
            }}
            left={
                <Stack spacing={2}>
                    <TextField
                        select
                        label={isFr ? 'Evenement' : 'Event'}
                        value={eventId}
                        onChange={(e) => {
                            setEventId(e.target.value)
                            setResult(null)
                        }}
                    >
                        {events.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.title} {item.country ? `- ${item.country}` : ''}
                            </MenuItem>
                        ))}
                        {events.length === 0 && (
                            <MenuItem value="" disabled>
                                {loadingEvents
                                    ? (isFr ? 'Chargement...' : 'Loading...')
                                    : (isFr ? 'Aucun evenement actif' : 'No active events')}
                            </MenuItem>
                        )}
                    </TextField>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {gates.map((gate) => {
                            const selected = gate === gateName
                            return (
                                <Chip
                                    key={gate}
                                    label={`${gate} ${gateCounts[gate] || 0}`}
                                    onClick={() => setGateName(gate)}
                                    sx={{
                                        bgcolor: selected ? '#FF8A00' : 'rgba(91,46,255,0.22)',
                                        color: selected ? '#0B0620' : '#FFFFFF',
                                        fontWeight: 900,
                                        border: selected ? '1px solid #FF8A00' : '1px solid rgba(183,166,255,0.16)',
                                    }}
                                />
                            )
                        })}
                    </Stack>

                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                        <TextField
                            fullWidth
                            label={isFr ? 'Nom personnel porte' : 'Gate staff name'}
                            value={staffName}
                            onChange={(e) => setStaffName(e.target.value)}
                            placeholder="Gate A staff 1"
                        />
                        <TextField
                            fullWidth
                            label={isFr ? 'Code scanner facultatif' : 'Scanner code (optional)'}
                            value={staffCode}
                            onChange={(e) => setStaffCode(e.target.value)}
                            placeholder={isFr ? 'PIN porte facultatif' : 'Optional gate PIN'}
                            type="password"
                        />
                    </Stack>

                    <TextField
                        label={isFr ? 'Code billet ou reference' : 'Ticket code or reference'}
                        value={ticketCode}
                        onChange={(e) => setTicketCode(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault()
                                handleValidate()
                            }
                        }}
                        inputRef={scanInputRef}
                        placeholder={isFr ? 'Ex: TKT-2026-001' : 'Ex: TKT-2026-001'}
                    />

                    {result && (
                        <Alert severity={result.tone}>
                            <strong>{result.title}</strong>
                            <br />
                            {result.body}
                            {result.details && (
                                <Box sx={{ mt: 1 }}>
                                    <Typography sx={{ fontSize: 13 }}>
                                        {result.details.purchaser_name || 'Customer'} · {result.details.ticket_type || result.details.section_name || 'General Admission'}
                                    </Typography>
                                    <Typography sx={{ fontSize: 13 }}>
                                        {result.details.ticket_no} · {result.details.check_in_status || result.details.status}
                                    </Typography>
                                    {result.details.checked_in_at && (
                                        <Typography sx={{ fontSize: 13 }}>
                                            {isFr ? 'Heure originale' : 'Original time'}: {result.details.checked_in_at}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Alert>
                    )}

                    <Button
                        variant="outlined"
                        startIcon={<QrCodeScannerRoundedIcon />}
                        onClick={handleValidate}
                        sx={{
                            color: '#FFFFFF',
                            borderColor: 'rgba(183,166,255,0.34)',
                            borderRadius: 4,
                            textTransform: 'none',
                            fontWeight: 900,
                        }}
                    >
                        {isFr ? 'Scanner / valider' : 'Scan / Validate'}
                    </Button>
                </Stack>
            }
            right={
                <Stack spacing={2}>
                    {canManageStaff && (
                        <Box sx={shellStyles.softPanel}>
                            <Stack spacing={1.5}>
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <BadgeRoundedIcon sx={{ color: '#FF8A00' }} />
                                    <Box>
                                        <Typography sx={{ fontWeight: 800 }}>
                                            {isFr ? 'Acces personnel porte' : 'Gate Staff Access'}
                                        </Typography>
                                        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                            {isFr
                                                ? 'Autorisez un compte MojoPayr a scanner seulement cet evenement.'
                                                : 'Authorize a MojoPayr user to scan only this event or gate.'}
                                        </Typography>
                                    </Box>
                                </Stack>
                                <TextField
                                    size="small"
                                    label={isFr ? 'Nom' : 'Name'}
                                    value={staffInvite.name}
                                    onChange={(e) => setStaffInvite((current) => ({ ...current, name: e.target.value }))}
                                />
                                <TextField
                                    size="small"
                                    label={isFr ? 'Email MojoPayr' : 'MojoPayr email'}
                                    value={staffInvite.email}
                                    onChange={(e) => setStaffInvite((current) => ({ ...current, email: e.target.value }))}
                                />
                                <TextField
                                    size="small"
                                    label={isFr ? 'Telephone' : 'Phone'}
                                    value={staffInvite.phone}
                                    onChange={(e) => setStaffInvite((current) => ({ ...current, phone: e.target.value }))}
                                />
                                <Button
                                    variant="outlined"
                                    onClick={handleSaveStaff}
                                    disabled={savingStaff}
                                    sx={{
                                        color: '#FF8A00',
                                        borderColor: 'rgba(255,138,0,0.5)',
                                        borderRadius: 3,
                                        textTransform: 'none',
                                        fontWeight: 900,
                                    }}
                                >
                                    {savingStaff
                                        ? (isFr ? 'Enregistrement...' : 'Saving...')
                                        : (isFr ? 'Autoriser le personnel' : 'Authorize Staff')}
                                </Button>
                                {gateStaff.slice(0, 4).map((staff) => (
                                    <Box
                                        key={staff.id}
                                        sx={{
                                            p: 1.25,
                                            borderRadius: 3,
                                            bgcolor: 'rgba(91,46,255,0.18)',
                                            border: '1px solid rgba(183,166,255,0.12)',
                                        }}
                                    >
                                        <Typography sx={{ fontWeight: 800, fontSize: 13 }}>
                                            {staff.staff_name || staff.staff_email || staff.staff_phone}
                                        </Typography>
                                        <Typography sx={{ color: 'text.secondary', fontSize: 12 }}>
                                            {staff.event_title || (isFr ? 'Tous les evenements' : 'All events')} - {staff.gate_name || (isFr ? 'Toute porte' : 'Any gate')}
                                        </Typography>
                                    </Box>
                                ))}
                            </Stack>
                        </Box>
                    )}
                    <Box sx={shellStyles.softPanel}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <QrCodeScannerRoundedIcon sx={{ color: '#FF8A00' }} />
                            <Box>
                                <Typography sx={{ fontWeight: 800 }}>
                                    {isFr ? 'Mode scanner seul' : 'Scanner-only mode'}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    {isFr
                                        ? 'Le personnel valide uniquement les billets pour cet evenement et cette porte.'
                                        : 'Staff only validate tickets for this event and selected gate.'}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                    <Box sx={shellStyles.softPanel}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <VerifiedRoundedIcon sx={{ color: '#FF8A00' }} />
                            <Box>
                                <Typography sx={{ fontWeight: 800 }}>
                                    {isFr ? 'Porte active' : 'Active gate'}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    {event ? `${gateName} - ${gateCounts[gateName] || 0} checked` : (isFr ? 'Aucun evenement' : 'No event')}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                    <Box sx={shellStyles.softPanel}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <BadgeRoundedIcon sx={{ color: '#FF8A00' }} />
                            <Box>
                                <Typography sx={{ fontWeight: 800 }}>
                                    {isFr ? 'Personnel' : 'Staff'}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    {staffName.trim() || (isFr ? 'Aucun personnel' : 'No staff assigned')}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                    <Box sx={shellStyles.softPanel}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                            <ErrorOutlineRoundedIcon sx={{ color: '#FF8A00' }} />
                            <Box>
                                <Typography sx={{ fontWeight: 800 }}>
                                    {isFr ? 'Total entree' : 'Total checked'}
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                    {attendanceCount} {isFr ? 'billets valides' : 'tickets validated'}
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Stack>
            }
        />
    )
}
